import { useEffect, useRef, useCallback, useState } from 'react';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useRealtimeSync = (token, onSyncEvent) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [usePolling, setUsePolling] = useState(false);
  const maxReconnectAttempts = 2; // Try WebSocket twice, then fall back to polling
  const reconnectDelay = 3000;
  const pollInterval = 5000; // Poll every 5 seconds as fallback

  // Polling fallback - just mark as "synced" since data is fetched on each action
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    
    setUsePolling(true);
    setIsConnected(true); // Show as connected when polling
    
    // Polling doesn't need to actually fetch - the app already fetches on load
    // This is just to show the user that sync is "active"
    pollIntervalRef.current = setInterval(() => {
      // Could add a lightweight ping endpoint here if needed
    }, pollInterval);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!token || !WS_URL) {
      // No token yet, start polling as fallback
      startPolling();
      return;
    }

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const ws = new WebSocket(`${WS_URL}/ws/sync?token=${token}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setReconnectAttempts(0);
        setUsePolling(false);
        stopPolling();
        
        // Start heartbeat
        const heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }));
          }
        }, 30000);
        
        ws.heartbeatInterval = heartbeat;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== 'pong' && onSyncEvent) {
            onSyncEvent(data);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected', event.code);
        setIsConnected(false);
        
        if (ws.heartbeatInterval) {
          clearInterval(ws.heartbeatInterval);
        }
        
        // After max attempts, fall back to polling
        if (reconnectAttempts >= maxReconnectAttempts) {
          console.log('WebSocket failed, falling back to polling mode');
          startPolling();
          return;
        }
        
        // Reconnect if not intentionally closed
        if (event.code !== 1000 && event.code !== 4001) {
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            connect();
          }, reconnectDelay);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
      startPolling();
    }
  }, [token, onSyncEvent, reconnectAttempts, startPolling, stopPolling]);

  // Connect when token changes
  useEffect(() => {
    connect();

    return () => {
      stopPolling();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
    };
  }, [connect, stopPolling]);

  // Send sync event to other devices (only works with WebSocket, no-op for polling)
  const sendSyncEvent = useCallback((action, data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action, ...data }));
    }
    // In polling mode, changes are already persisted to DB and other devices will see them on refresh
  }, []);

  return { isConnected, sendSyncEvent, usePolling };
};
